import { useState, useEffect, useCallback } from 'react'
import panelManager from '../utils/PanelManager.js'

/**
 * Hook for managing panel state with the global PanelManager
 * @param {string} panelId - Unique identifier for the panel
 * @returns {Object} Panel management functions and state
 */
export const usePanelManager = (panelId) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [data, setData] = useState(null)

  // Close callback for the panel manager
  const closePanel = useCallback(() => {
    setIsOpen(false)
    setPosition({ x: 0, y: 0 })
    setData(null)
  }, [])

  // Register panel with manager on mount
  useEffect(() => {
    panelManager.registerPanel(panelId, closePanel)
    
    // Listen for panel manager events
    const unsubscribe = panelManager.addObserver((action, targetPanelId, eventData) => {
      if (targetPanelId === panelId) {
        switch (action) {
          case 'open':
            setIsOpen(true)
            if (eventData?.position) setPosition(eventData.position)
            if (eventData?.data) setData(eventData.data)
            break
          case 'close':
            setIsOpen(false)
            setPosition({ x: 0, y: 0 })
            setData(null)
            break
        }
      }
    })

    return () => {
      unsubscribe()
      panelManager.unregisterPanel(panelId)
    }
  }, [panelId, closePanel])

  // Open panel function
  const openPanel = useCallback((newPosition, newData) => {
    panelManager.openPanel(panelId, newPosition, newData)
  }, [panelId])

  // Switch to panel function (always opens)
  const switchToPanel = useCallback((newPosition, newData) => {
    panelManager.switchToPanel(panelId, newPosition, newData)
  }, [panelId])

  // Close panel function
  const handleClosePanel = useCallback(() => {
    panelManager.closePanel(panelId)
  }, [panelId])

  // Get z-index for the panel
  const zIndex = panelManager.getPanelZIndex(panelId)

  return {
    isOpen,
    position,
    data,
    zIndex,
    openPanel,
    switchToPanel,
    closePanel: handleClosePanel,
    // Utility functions
    closeAllPanels: () => panelManager.closeAllPanels(),
    isPanelOpen: (id) => panelManager.isPanelOpen(id)
  }
}

export default usePanelManager